export interface StockBalance {
  id: string;
  productId: string;
  warehouseId: string;
  onHand: number;
  reserved?: number;
  inTransit?: number;
  stockMin?: number;
  stockMax?: number;
  reorderPoint?: number;
  active?: boolean;
  created?: string | Date;
  updated?: string | Date;
}

export interface StockBalanceCreatePayload {
  productId: string;
  warehouseId: string;
  onHand?: number;
  reserved?: number;
  inTransit?: number;
  stockMin?: number;
  stockMax?: number;
  reorderPoint?: number;
  active?: boolean;
}

export interface StockBalanceUpdatePayload
  extends Partial<Omit<StockBalanceCreatePayload, 'productId' | 'warehouseId'>> {}

export interface StockBalanceQueryResult {
  items: StockBalance[];
  total: number;
  hasMore?: boolean;
  nextCursor?: string | null;
}

/** Deterministic document id for product × warehouse balance rows. */
export function stockBalanceDocId(productId: string, warehouseId: string): string {
  return `${productId}__${warehouseId}`;
}

/** Sellable quantity from on-hand minus reserved. */
export function computeSellableQuantity(
  onHand: number,
  reserved = 0
): number {
  const sellable = onHand - reserved;
  return Number.isFinite(sellable) ? sellable : 0;
}

/** Org-wide sellable total from per-warehouse balances. */
export function computeAggregateAvailability(
  balances: Array<Pick<StockBalance, 'onHand' | 'reserved' | 'active'>>
): number {
  return balances.reduce((sum, row) => {
    if (row.active === false) return sum;
    return sum + Math.max(0, computeSellableQuantity(row.onHand ?? 0, row.reserved ?? 0));
  }, 0);
}
